/** Small, optional list snapshot for the monitor's first paint. Never a live baseline. */
const KEY = "flap_monitor_list_v1";
const MAX_AGE_MS = 24 * 60 * 60 * 1000;
const CLOCK_SKEW_MS = 5 * 60 * 1000;
const MAX_ROWS = 4000;
const MAX_BYTES = 3 * 1024 * 1024;
const ADDRESS = /^0x[a-f0-9]{40}$/i;
const HASH = /^0x[a-f0-9]{64}$/i;
const TEXT_FIELDS = {
  name: 300, symbol: 100, factoryName: 300, category: 40, image: 2048, meta: 2048,
  quoteSymbol: 100, quoteTokenSymbol: 100,
};
const ADDRESS_FIELDS = ["factory", "vault", "quoteToken", "quoteTokenAddress", "quoteAddress"];
const NUMBER_FIELDS = ["block", "status", "listedBlock"];
const TIME_FIELDS = ["time", "listedAt", "listedDetectedAt"];
const BOOLEAN_FIELDS = ["listed", "timeExact", "listedExact", "listedAtApprox"];

let pending = null;
let scheduled = null;

function validNumber(value) {
  return typeof value === "number" && Number.isFinite(value) && value >= 0 && value <= Number.MAX_SAFE_INTEGER;
}

function cleanSnapshot(snapshot, now) {
  if (!snapshot || typeof snapshot !== "object" || !Array.isArray(snapshot.rows) ||
    !validNumber(snapshot.updatedAt) || snapshot.updatedAt * 1000 > now + CLOCK_SKEW_MS) return null;
  let changed = snapshot.rows.length > MAX_ROWS;
  let etag = typeof snapshot.etag === "string" && snapshot.etag.length <= 256 && !/[\r\n]/.test(snapshot.etag) ? snapshot.etag : "";
  const rows = [];
  const seen = new Set();
  for (const source of snapshot.rows.slice(0, MAX_ROWS)) {
    if (!source || typeof source !== "object" || Array.isArray(source) ||
      typeof source.token !== "string" || !ADDRESS.test(source.token)) {
      changed = true;
      continue;
    }
    const token = source.token.toLowerCase();
    if (seen.has(token)) { changed = true; continue; }
    seen.add(token);
    const row = { token };
    for (const [field, maxLength] of Object.entries(TEXT_FIELDS)) {
      const value = source[field];
      if (value == null) continue;
      if (typeof value !== "string") { changed = true; continue; }
      row[field] = value.slice(0, maxLength);
      if (value.length > maxLength) changed = true;
    }
    for (const field of ADDRESS_FIELDS) {
      const value = source[field];
      if (value == null || value === "") continue;
      if (typeof value === "string" && ADDRESS.test(value)) row[field] = value.toLowerCase();
      else changed = true;
    }
    for (const field of ["tx", "listedTx"]) {
      const value = source[field];
      if (value == null || value === "") continue;
      if (typeof value === "string" && HASH.test(value)) row[field] = value;
      else changed = true;
    }
    for (const field of NUMBER_FIELDS) {
      const value = source[field];
      if (value == null) continue;
      if (validNumber(value)) row[field] = value;
      else changed = true;
    }
    for (const field of TIME_FIELDS) {
      const value = source[field];
      if (value == null) continue;
      if (validNumber(value) && value <= now + CLOCK_SKEW_MS) row[field] = value;
      else changed = true;
    }
    for (const field of BOOLEAN_FIELDS) {
      const value = source[field];
      if (value == null) continue;
      if (typeof value === "boolean") row[field] = value;
      else changed = true;
    }
    rows.push(row);
  }
  if (snapshot.rows.length && !rows.length) return null;
  // A partial list must not send a validator that could leave it stuck on a 304.
  if (changed) etag = "";
  return { rows, updatedAt: snapshot.updatedAt, etag };
}

/** updatedAt is the server's seconds timestamp; cache at is milliseconds. */
export function readMonitorListCache() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw || raw.length > MAX_BYTES || new TextEncoder().encode(raw).byteLength > MAX_BYTES) return null;
    const cached = JSON.parse(raw);
    const now = Date.now();
    if (!validNumber(cached?.at) || cached.at <= 0 || now - cached.at > MAX_AGE_MS || cached.at > now + CLOCK_SKEW_MS) return null;
    return cleanSnapshot(cached, now);
  } catch { return null; }
}

/** Coalesce successive snapshots and serialize outside the current render turn. */
export function scheduleMonitorListCache(snapshot) {
  pending = snapshot;
  if (scheduled !== null) return;
  if (typeof window !== "undefined" && typeof window.requestIdleCallback === "function") {
    scheduled = { kind: "idle", id: window.requestIdleCallback(() => {
      scheduled = null;
      flushMonitorListCache();
    }, { timeout: 1500 }) };
  } else {
    scheduled = { kind: "timeout", id: setTimeout(() => {
      scheduled = null;
      flushMonitorListCache();
    }, 0) };
  }
}

/** Optional pagehide/test hook: write the latest pending snapshot immediately. */
export function flushMonitorListCache() {
  if (scheduled !== null) {
    if (scheduled.kind === "idle") window.cancelIdleCallback?.(scheduled.id);
    else clearTimeout(scheduled.id);
    scheduled = null;
  }
  const snapshot = pending;
  pending = null;
  if (!snapshot) return false;
  try {
    const now = Date.now();
    const clean = cleanSnapshot(snapshot, now);
    if (!clean) return false;
    const payload = { ...clean, at: now };
    const encoder = new TextEncoder();
    let raw = JSON.stringify(payload);
    let bytes = encoder.encode(raw).byteLength;
    while (bytes > MAX_BYTES && payload.rows.length) {
      const keep = Math.max(0, Math.min(payload.rows.length - 1, Math.floor(payload.rows.length * MAX_BYTES / bytes * 0.95)));
      payload.rows.length = keep;
      payload.etag = "";
      raw = JSON.stringify(payload);
      bytes = encoder.encode(raw).byteLength;
    }
    if (bytes > MAX_BYTES) return false;
    localStorage.setItem(KEY, raw);
    return true;
  } catch { return false; }
}
